import React, { useState, useEffect, useRef } from "react";
import DashboardFilter from "./Filters/DashboardFilter";
import Page from "components/Page";
import Card from "components/Card";
import Chat from "./Chat/Chat";
import styles from "./Dashboard.module.scss";
import Tasks from "./Tasks/Tasks";
import Notes from "./Notes/Notes";
import RequestApproval from "./RequestApproval";
import SchedulerDashboard from "./SchedulerDashboard/SchedulerDashboard";
import useDashBoardModule from "./hooks/DashBoardModule";
import { withTranslation } from "react-i18next";
import { cacheSideBarActive, encodeId, getStorage } from "utils";
import constants from "../../../constants";
import { fullOrFistnameKeys } from "../Scheduler/OfficeSchedulerTab/SubTabs/StaffAvailabilityTab";
import useModuleDisplayOwner, {
  getModuleDisplayOwnerName,
} from "hooks/useModuleDisplayOwner";

function ManagerDashboard({ t, history }) {
  const getPersistedView = () => {
    const saved = sessionStorage.getItem(constants.dashboardSchedularView);
    return saved || "1";
  };

  const [isSidebarActive, setSidebarActive] = useState(
    cacheSideBarActive(constants.sidebar.cache.dashboardIsSidebarActive)
  );

  const [
    staffAvailabilityViewSelectedFromDashBoard,
    setStaffAvailabilityViewSelectedFromDashBoard,
  ] = useState(getPersistedView());

  const isFromOwnerOfficeModule = getStorage("isFromOwnerOfficeModule");
  const scrollGuardActive = useRef(true);

  useEffect(() => {
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    const resetScroll = () => {
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      const mainContent = document.querySelector(".main-content");
      if (mainContent) mainContent.scrollTop = 0;
      document
        .querySelectorAll(".scheduler-page, .page .container")
        .forEach((el) => {
          if (el && el.scrollTop !== undefined) el.scrollTop = 0;
        });
    };

    const mainContentEl = document.querySelector(".main-content");

    const handleScroll = () => {
      if (!scrollGuardActive.current) return;
      const windowY =
        window.scrollY ?? document.documentElement?.scrollTop ?? 0;
      const mainY = mainContentEl ? mainContentEl.scrollTop : 0;
      if (windowY > 5 || mainY > 5) {
        resetScroll();
      }
    };

    resetScroll();
    const id = requestAnimationFrame(resetScroll);
    const timeouts = [0, 50, 100, 200, 400, 700, 1200].map((ms) =>
      setTimeout(resetScroll, ms)
    );
    const guardEnd = setTimeout(() => {
      scrollGuardActive.current = false;
    }, 2000);

    window.addEventListener("scroll", handleScroll, { passive: false });
    if (mainContentEl)
      mainContentEl.addEventListener("scroll", handleScroll, {
        passive: false,
      });

    return () => {
      cancelAnimationFrame(id);
      timeouts.forEach(clearTimeout);
      clearTimeout(guardEnd);
      window.removeEventListener("scroll", handleScroll);
      if (mainContentEl)
        mainContentEl.removeEventListener("scroll", handleScroll);
      window.history.scrollRestoration = prev;
    };
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      constants.dashboardSchedularView,
      staffAvailabilityViewSelectedFromDashBoard
    );
  }, [staffAvailabilityViewSelectedFromDashBoard]);

  const handleSidebarToggle = () => {
    setSidebarActive(!isSidebarActive);
    localStorage.setItem(
      constants.sidebar.cache.dashboardIsSidebarActive,
      !isSidebarActive
    );
  };

  const goBack = () => {
    let routing = "";
    if (isFromOwnerOfficeModule?.officeSlection) {
      routing = constants.routes.accountOwner.officeOptions;
      history.push({
        pathname: routing.replace(
          ":officeId",
          encodeId(isFromOwnerOfficeModule.officeId)
        ),
      });
      return;
    } else if (isFromOwnerOfficeModule?.isAdminControl) {
      routing = constants.routes.staff.officeAdmin;
      history.push({
        pathname: routing.replace(
          ":officeId",
          encodeId(isFromOwnerOfficeModule.officeId)
        ),
      });
      return;
    } else if (isFromOwnerOfficeModule?.staffOfficeSlection) {
      routing = constants.routes.staff.officeOptions;
      history.push({
        pathname: routing.replace(
          ":officeId",
          encodeId(isFromOwnerOfficeModule.officeId)
        ),
      });
      return;
    } else {
      history.push("/");
    }
  };

  const {
    data: {
      officeFilter,
      staffFilter,
      agendaTypes,
      viewSettig,
      selectedRoles,
      appliedOfficeFilters,
      appliedStaffFilters,
      appliedAgendaTypes,
      appliedViewSettig,
      ownerId,
      ownerData,
      isFilterApply,
      currentDate,
      selectedOwner,
      isSchedulerOnly,
      userList,
      otherStaffId,
      showEnterpriseEvents,
      isEnterpriseFilterActive,
      subscriptionValues: {
        isTaskFeatureEnabled,
        isNotesAndReminderFeatureEnabled,
        isSchedulerFeatureEnabled,
        isPatientConversationFeatureEnabled,
        isTeamConversationFeatureEnabled,
      },
      isAccountOwner,
      isStaff,
      profile,
    },
    methods: {
      setOfficeFilter,
      setStaffFilter,
      setSelectedRoles,
      setAgendaTypes,
      setViewSetting,
      setappliedViewSettig,
      handleApplyFilter,
      resetFilter,
      resetSearchTrigger,
      onDateChange,
      onActiveStartDateChange,
      setIsSchedulerOnly,
      handleToggleScheduler,
      setUserList,
      setShowEnterpriseEvents,
      setIsEnterpriseFilterActive,
    },
  } = useDashBoardModule();
  const displayOwner = useModuleDisplayOwner();
  const handleStaffAvailabilityViewChange = (newView) => {
    setStaffAvailabilityViewSelectedFromDashBoard(newView);
  };
  const selectedId = ownerId;
  const cacheIsMySchedule = getStorage(constants.notes.cache.isMySchedule);
  const [isMySchedule, setIsMySchedule] = useState(cacheIsMySchedule || false);
  useEffect(() => {
    if (
      staffAvailabilityViewSelectedFromDashBoard !== "3" &&
      appliedViewSettig.some((res) => fullOrFistnameKeys.includes(res))
    ) {
      const values = appliedViewSettig.filter(
        (res) => !fullOrFistnameKeys.includes(res)
      );
      setViewSetting(values);
      setappliedViewSettig(values);
    }
  }, [staffAvailabilityViewSelectedFromDashBoard]);
  return (
    <Page
      className="scheduler-page"
      scrollToTopOnMount={false}
      onBack={() => {
        goBack();
      }}
    >
      <div className={styles.title_change_btn_wrapper+"  flex-column align-items-start"}>
        <h2 className="page-title mb-0 margin-fix">
          {isAccountOwner || profile?.isAdmin
            ? t("dashBoard.managerDashBoard")
            : t("dashBoard.teamDashboard")}
        </h2>
                {isStaff && displayOwner?.id && (
          <div className="show-owner-name-text">
              {t("dashBoard.showDashBoard")}
              <span>
                {` '${getModuleDisplayOwnerName(displayOwner)}' `}
              </span>
              {t("dashBoard.office")}
          </div>
        )}
      </div>

      <Card className={styles.manager_dashboard_card + " " + "margin30"}>
        <div
          className={`${styles.dashboard_card_filter_container} scheduler-tabs-main-wrapper m-0 `}
          id="managerDashboardTargetElement"
        >
          <DashboardFilter
            isSidebarActive={isSidebarActive}
            handleSidebarToggle={handleSidebarToggle}
            officeFilter={officeFilter}
            setOfficeFilter={setOfficeFilter}
            staffFilter={staffFilter}
            setStaffFilter={setStaffFilter}
            handleApplyFilter={handleApplyFilter}
            resetFilter={resetFilter}
            setSelectedRoles={setSelectedRoles}
            selectedRoles={selectedRoles}
            ownerId={ownerId}
            ownerData={ownerData}
            resetSearchTrigger={resetSearchTrigger}
            isFilterApply={isFilterApply}
            currentDate={currentDate}
            onDateChange={onDateChange}
            onActiveStartDateChange={onActiveStartDateChange}
            agendaTypes={agendaTypes}
            viewSettig={viewSettig}
            setAgendaTypes={setAgendaTypes}
            setViewSetting={setViewSetting}
            isSchedulerOnly={isSchedulerOnly}
            setIsSchedulerOnly={setIsSchedulerOnly}
            handleToggleScheduler={handleToggleScheduler}
            staffAvailabilityViewSelectedFromDashBoard={
              staffAvailabilityViewSelectedFromDashBoard
            }
            setUserList={setUserList}
            setIsEnterpriseFilterActive={setIsEnterpriseFilterActive}
            setShowEnterpriseEvents={setShowEnterpriseEvents}
            isEnterpriseFilterActive={isEnterpriseFilterActive}
            showEnterpriseEvents={showEnterpriseEvents}
            isMySchedule={isMySchedule}
          />
          <div className={styles.dashboad_right_container}>
            <div className={styles.grid_system}>
              <div className={styles.box}>
                <SchedulerDashboard
                  currentDate={currentDate}
                  onDateChange={onDateChange}
                  appliedAgendaTypes={appliedAgendaTypes}
                  appliedViewSettig={appliedViewSettig}
                  appliedOfficeFilters={appliedOfficeFilters}
                  appliedStaffFilters={appliedStaffFilters}
                  isSidebarActive={isSidebarActive}
                  handleSidebarToggle={handleSidebarToggle}
                  isSchedulerOnly={isSchedulerOnly}
                  staffAvailabilityViewSelectedFromDashBoard={
                    staffAvailabilityViewSelectedFromDashBoard
                  }
                  setStaffAvailabilityViewSelectedFromDashBoard={
                    handleStaffAvailabilityViewChange
                  }
                  userList={userList}
                  otherStaffId={otherStaffId}
                  isEnterpriseFilterActive={isEnterpriseFilterActive}
                  selectedOwnerId={selectedId}
                  isSchedulerFeatureEnabled={isSchedulerFeatureEnabled}
                  isMySchedule={isMySchedule}
                  setIsMySchedule={setIsMySchedule}
                />
              </div>
              <div className={styles.box}>
                <Chat
                  appliedOfficeFilters={
                    !isSchedulerOnly ? appliedOfficeFilters : []
                  }
                  selectedOwner={selectedOwner}
                  isStaff={isStaff}
                  profile={profile}
                  isOwner={isAccountOwner}
                  isPatientConversationFeatureEnabled={
                    isPatientConversationFeatureEnabled
                  }
                  isTeamConversationFeatureEnabled={
                    isTeamConversationFeatureEnabled
                  }
                />
              </div>
              <div className={styles.box}>
                <Notes
                  appliedOfficeFilters={appliedOfficeFilters}
                  appliedStaffFilters={appliedStaffFilters}
                  isSchedulerOnly={isSchedulerOnly}
                  isNotesAndReminderFeatureEnabled={
                    isNotesAndReminderFeatureEnabled
                  }
                  dashBoardSelectedOwner={selectedOwner}
                />
              </div>
              <div className={styles.box}>
                <RequestApproval
                  appliedOfficeFilters={appliedOfficeFilters}
                  appliedStaffFilters={appliedStaffFilters}
                  isSchedulerOnly={isSchedulerOnly}
                  isStaff={isStaff}
                  isOwner={isAccountOwner}
                />
              </div>
              <div className={styles.box}>
                <Tasks
                  appliedOfficeFilters={appliedOfficeFilters}
                  appliedStaffFilters={appliedStaffFilters}
                  selectedOwner={selectedOwner}
                  isSchedulerOnly={isSchedulerOnly}
                  isFromDashboard
                  isTaskFeatureEnabled={isTaskFeatureEnabled}
                  isStaff={isStaff}
                  isOwner={isAccountOwner}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

    </Page>
  );
}

export default withTranslation()(ManagerDashboard);
